import { QueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { filterDeep } from 'deepdash-es/standalone';
import { cloneDeep, uniqBy } from 'lodash';
import { all, put, select, takeEvery } from 'redux-saga/effects';
import { Automation, AutomationType } from 'api/automations';
import { CustomAutomationsApi } from 'api/automations/custom-automation-api';
import { Role } from 'api/models';
import { toastService } from 'components/molecules/toast';
import { EffortPlanningConfig } from 'components/organisms/effort-planning-modal/types';
import {
  PhaseEntityType,
  converToEntityId,
  extractParts,
} from 'components/organisms/projects/project-plan/phase-entity';
import {
  getAutoAllocatedAllocationsFromPhases,
  getAutoAllocatedAllocationsFromTasks,
  getAutoAllocatedAssignees,
  getAutoAllocatedAssigneesFromPhases,
} from 'components/organisms/templates/projects/resource-management/utils/auto-allocate';
import { RootState } from 'data';
import { StoreActionProps } from 'data/core';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import {
  AutomationLiteForTemplate,
  TemplateObjectType,
  TemplatesAPI,
} from 'data/templates/api';
import { saveDocumentTemplate } from 'data/templates/documents/sagas/_save-template';
import { addUpdateTemplateDraftIndexedDb } from 'hooks/use-idb';
import {
  ROLES_QUERY_KEY,
  ROLES_WITH_PLACEHOLDER_QUERY_KEY,
} from 'queries/roles';
import { logger } from 'services';
import { TeamsStore } from 'stores/teams';
import { rlNavigation } from 'utils/navigation';
import {
  CreateOrUpdateProjectTemplateApiRequest,
  changeTemplateSaveState,
  clearProjectTemplate,
  fetchProjectTemplateStartSuccess,
} from '..';
import {
  saveProjectTemplate,
  SaveProjectTemplatePayload,
  // saveProjectTemplateWithSheetData,
  // SaveProjectTemplateWithSheetDataPayload,
  saveProjectTemplateWithSheetTabData,
  SaveProjectTemplateWithSheetTabDataPayload,
} from '../actions';
import {
  denormalizePayload,
  NormalizedProjectTemplatePayload,
  setDynamicConditionInPayload,
  validatePhaseShiftInPayload,
} from '../api/transform';
import { updateProjectTemplateTask } from '../slices';
import {
  ProjectTemplateApi,
  ProjectTemplateData,
  ProjectTemplateSpace,
  TemplateAllocation,
} from '../types';
import { findLargestSumOfStartDayDuration, findLowestStartDay } from '../utils';
import { generateNewTaskRows } from './utils';

export const isAutomationLoaded = (
  automation: Automation | AutomationLiteForTemplate,
): automation is Automation =>
  automation !== undefined && (automation as any)?.actionBlocks !== undefined;

export const getActiveProjectTemplate = ({
  templates,
}: RootState): NormalizedProjectTemplatePayload =>
  templates.project.data[templates.project.activeId];

function* saveAllSpaceTabs(spaces: ProjectTemplateSpace[]) {
  const saveAllSpaces: any[] = [];

  spaces.forEach((space) => {
    space.spaceTabs.forEach((tab) => {
      saveAllSpaces.push(saveDocumentTemplate(tab.spaceTabId, true));
    });
  });

  yield all(saveAllSpaces);
}

function* saveProjectTemplateWithSheetTabDataSaga(
  action: StoreActionProps<SaveProjectTemplateWithSheetTabDataPayload>,
) {
  const {
    payload: { sheetTabId, sheetTabName, assignee, taskId },
  } = action;
  const projectTemplate: NormalizedProjectTemplatePayload = yield select(
    getActiveProjectTemplate,
  );

  const newProjectTemplate = cloneDeep(projectTemplate);

  const task = newProjectTemplate.tasks[taskId];

  if (task?.sheetTemplate) {
    task.sheetTemplate.sheetTabs.forEach((tab) => {
      if (tab.sheetTabId === sheetTabId) {
        tab.sheetTabName = sheetTabName;
        tab.assignee = assignee;
      }
    });
  }

  yield put(
    updateProjectTemplateTask({
      task,
      isNew: false,
    }),
  );
}

function* saveProjectTemplateSaga(
  action: StoreActionProps<SaveProjectTemplatePayload>,
) {
  const {
    payload: {
      type,
      handleSuccess,
      saveToIdb,
      successCreatedMessage = 'Template created successfully!',
      successSavedMessage = 'Template saved successfully!',
      errorMessage = 'There was an error while saving the template',
    },
  } = action;

  const projectTemplate: NormalizedProjectTemplatePayload = yield select(
    getActiveProjectTemplate,
  );

  const queryClient = (yield getQueryClientContext()) as QueryClient;

  try {
    if (!saveToIdb) {
      yield put(
        changeTemplateSaveState({
          state: true,
          template: projectTemplate.template,
        }),
      );
    }
    const { autoAllocation: autoAllocate } =
      projectTemplate.template.templateContent;
    const { effortPlanning } = projectTemplate.template;
    const _template = setDynamicConditionInPayload(projectTemplate);
    const _phaseShiftValidTemplate = validatePhaseShiftInPayload(_template);

    const template = denormalizePayload(_phaseShiftValidTemplate);

    template.templateContent.projectPhases =
      filterDeep(
        template.templateContent.projectPhases,
        (object) => {
          if (!object) {
            return false;
          }
          if (object.isNew) {
            return false;
          }

          delete object.isNew;

          return true;
        },
        { childrenPath: ['tasks', 'subTasks'] },
      ) || [];

    template.templateContent.projectPhases.forEach((phase) => {
      return {
        ...phase,
        tasks: phase.tasks.map((task) => {
          return projectTemplate.tasks[task.taskId].dependentTasks;
        }),
      };
    });

    /* Start of entityOrder sanitization */

    // Populate all tasks across all phases, this is ok because ids of a given entity type will be unique even across phases
    // NOTE: Currently, the "Add new task/sprint/entity" row is stored as a task, when the user actually uses it to add a new entity, it will be converted to the correct entity type. And then a new "Add new task/sprint/entity" row will be added to take up the previous spot.

    const taskEntities = new Set(
      template.templateContent.projectPhases
        .map((phase) => [
          ...phase.tasks.map((task) =>
            converToEntityId(task.taskId, PhaseEntityType.Task),
          ),
        ])
        .flat(),
    );

    template.templateContent.projectPhases =
      template.templateContent.projectPhases.map((phase) => {
        return {
          ...phase,
          assignees:
            effortPlanning === EffortPlanningConfig.Phase
              ? (phase.assignees ?? []).filter(
                  (assignee) => assignee.effort != null && assignee.effort > 0,
                )
              : undefined,
          isNewEntity: undefined,
          entityOrder:
            phase.entityOrder?.filter(
              (id) =>
                extractParts(id).entityType !== PhaseEntityType.Task ||
                taskEntities.has(id),
            ) ?? [],
        };
      });

    /* End of entityOrder sanitization */

    const largestDuration = findLargestSumOfStartDayDuration(
      template.templateContent
        .projectPhases as unknown[] as ProjectTemplateData[],
    );
    const lowestStartDay = findLowestStartDay(
      template.templateContent
        .projectPhases as unknown[] as ProjectTemplateData[],
    );

    const total = largestDuration - lowestStartDay;

    template.templateContent.duration = total;
    template.templateContent.startDay = lowestStartDay;
    template.state = type;
    template.templateContent.milestoneRevenueRecognitionConfigs =
      projectTemplate.template.templateContent.milestoneRevenueRecognitionConfigs;

    // Preserve compressTimeline setting from the original template
    template.compressTimeline =
      projectTemplate.template.compressTimeline ??
      projectTemplate.template.templateMeta?.compressTimeline ??
      false;

    // Preserve templateMeta if it exists
    if (projectTemplate.template.templateMeta) {
      template.templateMeta = projectTemplate.template.templateMeta;
    }

    if (autoAllocate === true) {
      if (effortPlanning === EffortPlanningConfig.Phase) {
        const phases = Object.values(projectTemplate.projectPhases);
        const allocatedMembers = getAutoAllocatedAssigneesFromPhases(phases);
        const allocationsWithType =
          getAutoAllocatedAllocationsFromPhases(phases);
        const allocations = allocationsWithType.map(
          ({ type: resourceType, ...rest }) => rest as TemplateAllocation,
        );

        template.templateContent.allocations = allocations;
        template.templateContent.allocatedMembers = allocatedMembers;
      } else {
        const tasks = Object.values(projectTemplate.tasks);
        const allocatedMembers = getAutoAllocatedAssignees(tasks);
        const allocationsWithType = getAutoAllocatedAllocationsFromTasks(tasks);
        const allocations = allocationsWithType.map(
          ({ type: resourceType, ...rest }) => rest as TemplateAllocation,
        );

        template.templateContent.allocations = allocations;
        template.templateContent.allocatedMembers = allocatedMembers;
      }
    } else {
      const allocationsWithType = Object.values(
        projectTemplate.allocations || {},
      );
      const allocations = allocationsWithType.map(
        ({ type: resourceType, ...rest }) => rest as TemplateAllocation,
      );

      template.templateContent.allocations = allocations;
      template.templateContent.allocatedMembers =
        projectTemplate.allocatedMembers;
    }
    /**
     * Since server saves the template json as dump without validation,
     * FE sorts the phases by startDay before server submit
     * to maintain the chronological order during project creation from template.
     */
    template.templateContent.projectPhases = [
      ...template.templateContent.projectPhases,
    ].sort((phase1, phase2) => phase1.startDay - phase2.startDay);
    // temporary code to remove duplicate spaces
    template.templateContent.spaceList = uniqBy(
      template.templateContent.spaceList,
      (x) => x.spaceId,
    );

    if (!saveToIdb) {
      yield saveAllSpaceTabs(template.templateContent.spaceList);
    }

    if (saveToIdb) {
      yield addUpdateTemplateDraftIndexedDb(
        template.templateId,
        template,
        TemplateObjectType.Project,
      );
    } else {
      const automationOps = template.automations
        ? (template.automations
            .filter((e) =>
              e.deleted ? e.automationId > 0 : isAutomationLoaded(e),
            )
            .map((e) => ({
              automation: e,
              action: e.deleted
                ? 'DELETE'
                : // This is an ugly check, but any other check will become ugly
                e.automationId > 0
                ? 'PUT'
                : 'POST',
            })) as CreateOrUpdateProjectTemplateApiRequest['automationOps'])
        : ([] as CreateOrUpdateProjectTemplateApiRequest['automationOps']);

      const updatedTemplate: ProjectTemplateApi =
        yield TemplatesAPI.save<CreateOrUpdateProjectTemplateApiRequest>({
          ...template,
          automationOps,
        });

      const fetchedAutomations =
        yield CustomAutomationsApi.getAllAutomationsForParams({
          projectTemplateId: updatedTemplate.templateId,
          automationType: AutomationType.CustomAutomation,
        });

      if (handleSuccess) {
        handleSuccess(updatedTemplate);
      }
      const templateWithNewTaskRows = generateNewTaskRows(updatedTemplate);

      const rolesWithPlaceholder = queryClient.getQueryData<Role[]>(
        ROLES_WITH_PLACEHOLDER_QUERY_KEY,
      );
      const roles = queryClient.getQueryData<Role[]>(ROLES_QUERY_KEY);
      const { byId: teamsById }: TeamsStore = yield select(
        ({ teams }: RootState) => teams,
      );

      const parsedTemplate = {
        ...templateWithNewTaskRows,
        automations: fetchedAutomations,
      };

      yield put(
        fetchProjectTemplateStartSuccess({
          template: parsedTemplate,
          rolesWithPlaceholder,
          allRoles: roles,
          teamsLookup: teamsById,
        }),
      );

      if (template.templateId === 'new') {
        yield put(clearProjectTemplate(projectTemplate.template));

        yield rlNavigation.push(
          `/templates/projects/${updatedTemplate.templateId}`,
        );

        toastService({ message: successCreatedMessage }).success();
      } else {
        toastService({ message: successSavedMessage }).success();
      }

      yield put(
        changeTemplateSaveState({
          state: false,
          template: {
            templateId: updatedTemplate.templateId,
          },
        }),
      );
    }
  } catch (error) {
    logger.error('an error while saving the template', { error });
    message.error(errorMessage);

    yield put(
      changeTemplateSaveState({
        state: false,
        template: projectTemplate.template,
      }),
    );
  }
}

export function* watchSaveProjectTemplateSaga(): Generator {
  yield takeEvery(saveProjectTemplate.type, saveProjectTemplateSaga);
}

export function* watchSaveProjectTemplateWithSheetTabDataSaga(): Generator {
  yield takeEvery(
    saveProjectTemplateWithSheetTabData.type,
    saveProjectTemplateWithSheetTabDataSaga,
  );
}
